
import { Component, Vue } from 'vue-property-decorator';

@Component
export default class ErrorBoundary extends Vue {
  public error: Error | null = null;
  public errorInfo: string = '';
  public showDetails: boolean = false;

  get errorMessage(): string {
    if (!this.error) return '';
    return this.error.message || 'Unknown error occurred';
  }

  /**
   * Vue error handler - catches errors from child components
   */
  public errorCaptured(err: Error, vm: Vue, info: string): boolean {
    console.error('ErrorBoundary caught error:', err);
    console.error('Error info:', info);
    console.error('Component:', vm);

    // Store error state
    this.error = err;
    this.errorInfo = info;

    // Log to console for debugging
    this.logError(err, info);

    // Prevent error from propagating further
    return false;
  }

  /**
   * Retry the failed operation
   */
  public handleRetry(): void {
    // Clear error state
    this.error = null;
    this.errorInfo = '';
    this.showDetails = false;

    // Emit retry event to parent
    this.$emit('retry');

    // Force re-render
    this.$forceUpdate();
  }

  /**
   * Reset to clean state
   */
  public handleReset(): void {
    // Clear error state
    this.error = null;
    this.errorInfo = '';
    this.showDetails = false;

    // Emit reset event to parent
    this.$emit('reset');

    // Force re-render
    this.$forceUpdate();
  }

  /**
   * Log error for monitoring/debugging
   */
  private logError(error: Error, info: string): void {
    // In production, you might want to send this to an error tracking service
    // like Sentry, LogRocket, etc.

    const errorLog = {
      message: error.message,
      stack: error.stack,
      info,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
    };

    console.error('Error boundary log:', errorLog);

    // TODO: Send to error tracking service in production
    // Example: Sentry.captureException(error, { contexts: { vue: { componentInfo: info } } });
  }
}
